import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import {
  Edit,
  Plus,
  Save,
  BarChart3,
  Target,
  Loader2
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { apiRequest } from '@/lib/queryClient';

interface AutomationBenchmark {
  id: string;
  task_name: string;
  pillar: string;
  baseline_hours: number;
  automated_hours: number;
  savings_percentage: number;
  frequency: string;
  description: string;
}

interface PillarBenchmark {
  pillar: string;
  average_automation: number;
  industry_best: number;
  typical_range_min: number;
  typical_range_max: number;
}

export default function AdminBenchmarksScreen() {
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [selectedBenchmark, setSelectedBenchmark] = useState<AutomationBenchmark | null>(null);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Fetch automation benchmarks
  const { data: automationBenchmarks = [], isLoading: benchmarksLoading } = useQuery({
    queryKey: ['/api/admin/automation-benchmarks'],
    queryFn: () => apiRequest('/api/admin/automation-benchmarks')
  });

  // Fetch pillar benchmarks
  const { data: pillarBenchmarks = [], isLoading: pillarsLoading } = useQuery({
    queryKey: ['/api/admin/pillar-benchmarks'],
    queryFn: () => apiRequest('/api/admin/pillar-benchmarks')
  });

  // Create automation benchmark mutation
  const createBenchmarkMutation = useMutation({
    mutationFn: (data: Partial<AutomationBenchmark>) =>
      apiRequest('/api/admin/automation-benchmarks', {
        method: 'POST',
        body: data
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/admin/automation-benchmarks'] });
      setIsCreateDialogOpen(false);
      toast({
        title: "Success",
        description: "Automation benchmark created successfully"
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to create benchmark",
        variant: "destructive"
      });
    }
  });

  // Update automation benchmark mutation
  const updateBenchmarkMutation = useMutation({
    mutationFn: ({ id, ...data }: AutomationBenchmark) =>
      apiRequest(`/api/admin/automation-benchmarks/${id}`, {
        method: 'PUT',
        body: data
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/admin/automation-benchmarks'] });
      setIsEditDialogOpen(false);
      setSelectedBenchmark(null);
      toast({
        title: "Success",
        description: "Automation benchmark updated successfully"
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update benchmark",
        variant: "destructive"
      });
    }
  });

  const handleCreateBenchmark = (formData: FormData) => {
    const data = {
      task_name: formData.get('task_name') as string,
      pillar: formData.get('pillar') as string,
      baseline_hours: parseFloat(formData.get('baseline_hours') as string),
      automated_hours: parseFloat(formData.get('automated_hours') as string),
      savings_percentage: parseFloat(formData.get('savings_percentage') as string),
      frequency: formData.get('frequency') as string,
      description: formData.get('description') as string
    };
    createBenchmarkMutation.mutate(data);
  };

  const handleUpdateBenchmark = (formData: FormData) => {
    if (!selectedBenchmark) return;
    
    const data = {
      ...selectedBenchmark,
      task_name: formData.get('task_name') as string,
      pillar: formData.get('pillar') as string,
      baseline_hours: parseFloat(formData.get('baseline_hours') as string),
      automated_hours: parseFloat(formData.get('automated_hours') as string),
      savings_percentage: parseFloat(formData.get('savings_percentage') as string),
      frequency: formData.get('frequency') as string,
      description: formData.get('description') as string
    };
    updateBenchmarkMutation.mutate(data);
  };

  const pillars = ['Account Reconciliation', 'Journal Entry', 'Intercompany', 'Close Management'];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Automation Benchmarks</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Manage automation benchmarks and pillar performance metrics
          </p>
        </div>
        <Button 
          onClick={() => setIsCreateDialogOpen(true)}
          className="bg-blue-600 hover:bg-blue-700 text-white"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Benchmark
        </Button>
      </div>

      <Tabs defaultValue="automation" className="space-y-4">
        <TabsList>
          <TabsTrigger value="automation">Automation Benchmarks</TabsTrigger>
          <TabsTrigger value="pillars">Pillar Benchmarks</TabsTrigger>
        </TabsList>

        <TabsContent value="automation">
          <Card>
            <CardHeader>
              <CardTitle>Automation Task Benchmarks</CardTitle>
              <CardDescription>
                Configure baseline and automated hours for specific tasks
              </CardDescription>
            </CardHeader>
            <CardContent>
              {benchmarksLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin" />
                  <span className="ml-2">Loading benchmarks...</span>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Task Name</TableHead>
                      <TableHead>Pillar</TableHead>
                      <TableHead>Baseline Hours</TableHead>
                      <TableHead>Automated Hours</TableHead>
                      <TableHead>Savings %</TableHead>
                      <TableHead>Frequency</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {automationBenchmarks.map((benchmark: AutomationBenchmark) => (
                      <TableRow key={benchmark.id}>
                        <TableCell className="font-medium">{benchmark.task_name}</TableCell>
                        <TableCell>{benchmark.pillar}</TableCell>
                        <TableCell>{benchmark.baseline_hours}</TableCell>
                        <TableCell>{benchmark.automated_hours}</TableCell>
                        <TableCell>{benchmark.savings_percentage}%</TableCell>
                        <TableCell>{benchmark.frequency}</TableCell>
                        <TableCell>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setSelectedBenchmark(benchmark);
                              setIsEditDialogOpen(true);
                            }}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="pillars">
          <Card>
            <CardHeader>
              <CardTitle>Pillar Performance Benchmarks</CardTitle>
              <CardDescription>
                Industry benchmarks for automation levels by pillar
              </CardDescription>
            </CardHeader>
            <CardContent>
              {pillarsLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin" />
                  <span className="ml-2">Loading pillar benchmarks...</span>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Pillar</TableHead>
                      <TableHead>Average Automation</TableHead>
                      <TableHead>Industry Best</TableHead>
                      <TableHead>Typical Range</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {pillarBenchmarks.map((pillar: PillarBenchmark) => (
                      <TableRow key={pillar.pillar}>
                        <TableCell className="font-medium">{pillar.pillar}</TableCell>
                        <TableCell>{pillar.average_automation}%</TableCell>
                        <TableCell>{pillar.industry_best}%</TableCell>
                        <TableCell>{pillar.typical_range_min}% - {pillar.typical_range_max}%</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Create Benchmark Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent className="max-w-md">
          <form onSubmit={(e) => {
            e.preventDefault();
            handleCreateBenchmark(new FormData(e.target as HTMLFormElement));
          }}>
            <DialogHeader>
              <DialogTitle>Create Automation Benchmark</DialogTitle>
              <DialogDescription>
                Add a new automation benchmark for task analysis
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div>
                <Label htmlFor="task_name">Task Name</Label>
                <Input
                  id="task_name"
                  name="task_name"
                  placeholder="e.g., Monthly Account Reconciliation"
                  required
                />
              </div>
              
              <div>
                <Label htmlFor="pillar">Pillar</Label>
                <select
                  id="pillar"
                  name="pillar"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  required
                >
                  <option value="">Select Pillar</option>
                  {pillars.map(pillar => (
                    <option key={pillar} value={pillar}>{pillar}</option>
                  ))}
                </select>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="baseline_hours">Baseline Hours</Label>
                  <Input
                    id="baseline_hours"
                    name="baseline_hours"
                    type="number"
                    step="0.1"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="automated_hours">Automated Hours</Label>
                  <Input
                    id="automated_hours"
                    name="automated_hours"
                    type="number"
                    step="0.1"
                    required
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="savings_percentage">Savings %</Label>
                  <Input
                    id="savings_percentage"
                    name="savings_percentage"
                    type="number"
                    step="0.1"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="frequency">Frequency</Label>
                  <select
                    id="frequency"
                    name="frequency"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    required
                  >
                    <option value="">Select Frequency</option>
                    <option value="Daily">Daily</option>
                    <option value="Weekly">Weekly</option>
                    <option value="Monthly">Monthly</option>
                    <option value="Quarterly">Quarterly</option>
                  </select>
                </div>
              </div>
              
              <div>
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  name="description"
                  placeholder="Describe the automation benchmark..."
                />
              </div>
            </div>
            
            <DialogFooter>
              <Button type="submit" disabled={createBenchmarkMutation.isPending}>
                {createBenchmarkMutation.isPending ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Save className="w-4 h-4 mr-2" />
                )}
                Create Benchmark
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Benchmark Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-md">
          {selectedBenchmark && (
            <form onSubmit={(e) => {
              e.preventDefault();
              handleUpdateBenchmark(new FormData(e.target as HTMLFormElement));
            }}>
              <DialogHeader>
                <DialogTitle>Edit Automation Benchmark</DialogTitle>
                <DialogDescription>
                  Update the automation benchmark details
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4 py-4">
                <div>
                  <Label htmlFor="edit_task_name">Task Name</Label>
                  <Input
                    id="edit_task_name"
                    name="task_name"
                    defaultValue={selectedBenchmark.task_name}
                    required
                  />
                </div>
                
                <div>
                  <Label htmlFor="edit_pillar">Pillar</Label>
                  <select
                    id="edit_pillar"
                    name="pillar"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    defaultValue={selectedBenchmark.pillar}
                    required
                  >
                    {pillars.map(pillar => (
                      <option key={pillar} value={pillar}>{pillar}</option>
                    ))}
                  </select>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="edit_baseline_hours">Baseline Hours</Label>
                    <Input
                      id="edit_baseline_hours"
                      name="baseline_hours"
                      type="number"
                      step="0.1"
                      defaultValue={selectedBenchmark.baseline_hours}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="edit_automated_hours">Automated Hours</Label>
                    <Input
                      id="edit_automated_hours"
                      name="automated_hours"
                      type="number"
                      step="0.1"
                      defaultValue={selectedBenchmark.automated_hours}
                      required
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="edit_savings_percentage">Savings %</Label>
                    <Input
                      id="edit_savings_percentage"
                      name="savings_percentage"
                      type="number"
                      step="0.1"
                      defaultValue={selectedBenchmark.savings_percentage}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="edit_frequency">Frequency</Label>
                    <select
                      id="edit_frequency"
                      name="frequency"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      defaultValue={selectedBenchmark.frequency}
                      required
                    >
                      <option value="Daily">Daily</option>
                      <option value="Weekly">Weekly</option>
                      <option value="Monthly">Monthly</option>
                      <option value="Quarterly">Quarterly</option>
                    </select>
                  </div>
                </div>
                
                <div>
                  <Label htmlFor="edit_description">Description</Label>
                  <Textarea
                    id="edit_description"
                    name="description"
                    defaultValue={selectedBenchmark.description}
                  />
                </div>
              </div>
              
              <DialogFooter>
                <Button type="submit" disabled={updateBenchmarkMutation.isPending}>
                  {updateBenchmarkMutation.isPending ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <Save className="w-4 h-4 mr-2" />
                  )}
                  Update Benchmark
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}